import { computed, effect, inject, Injectable } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, scan, share } from 'rxjs';
import { SimpleProductStepperStore } from '../simple-product-stepper.store';
import { IAdditionGroup } from '@core/models/shop.model';
import { RootStepperStore } from '../root-stepper.store';

@Injectable()
export class SimpleProductBlockAutoNextStepService {
  private readonly simpleProductStepperStore = inject(SimpleProductStepperStore);
  private readonly rootStepperStore = inject(RootStepperStore);

  constructor() {
    effect(
      () =>
        this.rootStepperStore.isAutoNextStepBlocked.update(state => ({
          ...state,
          isSimpleProductStepperBlocked: this.blockAutoNextStep(),
        })),
      {
        allowSignalWrites: true,
      },
    );
  }

  private readonly visitedStep$ = toObservable(
    this.simpleProductStepperStore.simpleProductStepper,
  ).pipe(
    map(stepper => {
      const selectedAdditionsGroupId = stepper?.selectedStepId;
      const additionGroups = stepper?.additionsGroups;
      if (!selectedAdditionsGroupId || !additionGroups) {
        return undefined;
      }
      return this.calculateAdditionGroupIndex({ additionGroups, selectedAdditionsGroupId });
    }),
    share(),
  );

  public readonly visitedStep = toSignal(this.visitedStep$);

  private readonly additionGroupStepBeforeBack = toSignal(
    this.visitedStep$.pipe(
      scan((acc, value) => {
        if (!value) {
          return acc;
        }
        if (!acc) {
          return value;
        }
        if (value.position > acc.position) {
          return value;
        }
        return acc;
      }),
    ),
  );

  /**
   * Computed signal that determines whether auto next step should be blocked.
   * Returns true if the current visited step position is less than the
   * addition group step before back position.
   */
  public readonly blockAutoNextStep = computed(() => {
    const visitedStep = this.visitedStep();
    const additionGroupStepBeforeBack = this.additionGroupStepBeforeBack();

    if (!visitedStep || !additionGroupStepBeforeBack) {
      return false;
    }

    return visitedStep.position < additionGroupStepBeforeBack.position;
  });

  private calculateAdditionGroupIndex({
    additionGroups,
    selectedAdditionsGroupId,
  }: {
    additionGroups: IAdditionGroup[];
    selectedAdditionsGroupId: string;
  }) {
    const index = additionGroups.findIndex(
      additionGroup => additionGroup.id === selectedAdditionsGroupId,
    );
    return {
      position: index,
    };
  }
}
